// src/pages/AdminInsights.jsx
import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line
} from "recharts";
import { getInsights } from "../services/AdminInsight";  

export default function AdminInsights() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    monthlySales: [],
    bestSelling: [],
    topCustomers: [],
  });
  const [error, setError] = useState(null);

  const fetchInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getInsights();
      setStats(data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  if (loading) return <div className="container py-5">Loading insights…</div>;
  if (error) return <div className="container py-5 text-danger">Error: {error}</div>;

  const { totalProducts, totalOrders, totalRevenue, monthlySales, bestSelling, topCustomers } = stats;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Admin Insights</h2>
        <div>
          <button className="btn btn-outline-secondary me-2" onClick={fetchInsights}>Refresh</button>
        </div>
      </div>

      {/* Overview cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card shadow-sm p-3">
            <small className="text-muted">Total Products</small>
            <h3 className="mt-2">{totalProducts}</h3>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm p-3">
            <small className="text-muted">Total Orders</small>
            <h3 className="mt-2">{totalOrders}</h3>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm p-3">
            <small className="text-muted">Revenue</small>
            <h3 className="mt-2">₹{Number(totalRevenue || 0).toFixed(2)}</h3>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="row gx-4 gy-4 mb-4">
        <div className="col-lg-8">
          <div className="card shadow-sm p-3" style={{height: 360}}>
            <h5>Monthly Revenue</h5>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart data={monthlySales}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `₹${Number(value).toFixed(2)}`} />
                <Bar dataKey="revenue" fill="#2d8f4f" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card shadow-sm p-3" style={{height: 360}}>
            <h5>Orders Trend</h5>
            <ResponsiveContainer width="100%" height="85%">
              <LineChart data={monthlySales}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="orders" stroke="#0ea5a6" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Best Selling Products */}
      <div className="row gx-4 gy-4 mb-4">
        <div className="col-lg-8">
          <div className="card shadow-sm p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Best Selling Products</h5>
              <small className="text-muted">top {bestSelling.length}</small>
            </div>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Quantity Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {bestSelling.map((p, idx) => (
                    <tr key={p.productId || idx}>
                      <td>{idx + 1}</td>
                      <td>{p.name}</td>
                      <td>{p.qty}</td>
                      <td>₹{Number(p.revenue || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  {bestSelling.length === 0 && (
                    <tr><td colSpan="4" className="text-center">No data</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Top Customers */}
        <div className="col-lg-4">
          <div className="card shadow-sm p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Top Customers</h5>
              <small className="text-muted">top {topCustomers.length}</small>
            </div>
            <div className="list-group">
              {topCustomers.map((c, idx) => (
                <div key={c.customerMobile || idx} className="list-group-item">
                  <div className="d-flex justify-content-between">
                    <div>
                      <strong>{c.customerName}</strong><br />
                      <small className="text-muted">{c.customerMobile}</small>
                    </div>
                    <div className="text-end">
                      <div>₹{Number(c.revenue || 0).toFixed(2)}</div>
                      <small className="text-muted">{c.orders} orders</small>
                    </div>
                  </div>
                </div>
              ))}
              {topCustomers.length === 0 && <div className="p-3 text-center text-muted">No data</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
